import React, { forwardRef, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  User,
  UsersSearchListQueryVariables,
  useUsersSearchListQuery,
} from 'graphql-common';
import {
  GetUrlParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import UserChip from '@lib/components/UserChip/UserChip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { FloatingLabelProps } from '@lib/hocs/withFloatingLabel';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import useChipListField from '@lib/hooks/useChipListField';
import getUserChipListFilters from './utils/getUserChipListFilters';
import styles from './UserChipListField.module.scss';

type Props = {
  actionId?: string;
  departmentId?: string;
  fieldName?: string;
  ids?: string[];
  interventionId?: string;
  sectorId?: string;
  siteId?: string;
  teamId?: string;
  values: Values;
};

const UserChipListField = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<unknown>) => {
    const {
      actionId = '',
      departmentId = '',
      fieldName = '',
      ids,
      interventionId = '',
      sectorId = '',
      siteId = '',
      teamId = '',
      values,
    } = props;
    const { t } = useTranslation();
    const [paginationParams, setPaginationParams] =
      useState<PaginationParams>(PAGINATION_PARAMS);

    const page = _get(paginationParams, GetUrlParams.Page);
    const perPage = _get(paginationParams, GetUrlParams.PerPage);
    const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
    const orderingDirection = _get(
      paginationParams,
      GetUrlParams.OrderingDirection,
    );

    const queryVariables: UsersSearchListQueryVariables = {
      page: Number(page),
      limit: Number(perPage),
    };

    const filters = getUserChipListFilters({
      interventionId,
      actionId,
      teamId,
      departmentId,
      sectorId,
      siteId,
    });
    if (filters.length) {
      queryVariables.filters = filters;
    }
    if (orderingField && orderingDirection) {
      queryVariables.sorts = {
        [orderingField]: orderingDirection,
      };
    }
    const skip = !filters.length;

    const query = useUsersSearchListQuery({
      fetchPolicy: 'no-cache',
      variables: queryVariables,
      skip,
    });

    const newItems = _get(
      query.data || query.previousData,
      'data.collection',
      [],
    ) as User[];

    const {
      visibleItems,
      newFormFieldValue,
      handleRemove,
      handleLoadMore,
      currentPage,
      totalPages,
      firstLoading,
      error,
    } = useChipListField<User>({
      fieldName,
      ids,
      values,
      paginationParams,
      setPaginationParams,
      queryResult: query,
      newItems,
    });

    if (error || firstLoading) return null;

    const newFormFieldValueRender = newFormFieldValue?.length
      ? newFormFieldValue
          .filter(
            (formFieldItem) =>
              _get(formFieldItem, 'data.id') &&
              !visibleItems.some(
                (visibleItem) =>
                  _get(formFieldItem, 'data.id') === visibleItem.id,
              ),
          )
          .map((formFieldItem: SelectOption) => (
            <UserChip
              user={formFieldItem.data as User}
              key={formFieldItem.value as string}
              rightIconComponent={
                <MaterialIcon
                  icon="cancel"
                  onClick={() => handleRemove(formFieldItem.value as string)}
                />
              }
            />
          ))
      : null;

    const visibleItemsRender = visibleItems?.length
      ? visibleItems.map((visibleItem) => (
          <UserChip
            user={visibleItem}
            key={visibleItem.id}
            rightIconComponent={
              <MaterialIcon
                icon="cancel"
                onClick={() => handleRemove(visibleItem.id)}
              />
            }
          />
        ))
      : null;

    if (newFormFieldValueRender === null && visibleItemsRender === null)
      return null;

    return (
      <div>
        <div className={styles.chipsList}>
          {newFormFieldValueRender}
          {visibleItemsRender}
        </div>
        {currentPage < totalPages && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={handleLoadMore}
            data-test-id="user-chip-list__show-more"
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
    );
  },
);

export default memo(UserChipListField);
